import { PlusOutlined } from '@ant-design/icons'
import type { ResidentItem } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit'
import { Tooltip } from 'antd'
import { NAMESPACE } from 'locale'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import Operations from '../../../../../operations'
import {
  ADD_MEDICATION_FORM_ID,
  createAddMedicationForm
} from '../AddMedicationScreen/addMedicationForm'
import { useAddMedicationButtonState } from '../hooks/useAddMedicationButtonState'
import { useAddMedicationFacelift } from '../hooks/useAddMedicationFacelift'

type AddMedicationResident = Pick<
  ResidentItem,
  'id' | 'status' | 'isActiveInitialAssessment'
>

type AddMedicationButtonProps = {
  resident?: AddMedicationResident
  dataTestId?: string
  style?: CSSProperties
}

export const AddMedicationButton = ({
  resident,
  dataTestId,
  style
}: AddMedicationButtonProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { disabled, tooltip } = useAddMedicationButtonState(resident)
  const { isEnabled: isFaceliftEnabled, isLoading: isLoadingFlags } =
    useAddMedicationFacelift()

  const handleClick = () => {
    if (!resident) return

    if (isFaceliftEnabled) {
      // `from` lets the page send the user back to whichever screen they
      // started on when they cancel or save
      navigate(`/residents/${resident.id}/emar/add-medication`, {
        state: { from: `${location.pathname}${location.search}` }
      })
      return
    }

    dispatch(
      Operations.loadList(
        'emar-round-timings',
        undefined,
        true,
        '/emar/legacy/emar-round-timings'
      )
    )
    dispatch(Operations.openModal({ modalId: ADD_MEDICATION_FORM_ID }))
    dispatch(createAddMedicationForm(resident.id))
  }

  const button = (
    <Button
      id="add-medication-button"
      onClick={handleClick}
      variant="primary"
      // Until the flags resolve we don't know which flow to open
      disabled={disabled || isLoadingFlags}
      style={style}
      data-testid={dataTestId}
    >
      <PlusOutlined />
      {t('ADD_MEDICATION')}
    </Button>
  )

  if (!tooltip) return button

  // A natively disabled <button> doesn't dispatch mouse events, so antd's
  // Tooltip never sees a hover — wrap it in a span so the span is what
  // actually receives the hover and triggers the tooltip.
  return (
    <Tooltip title={tooltip} placement="bottom">
      <span>{button}</span>
    </Tooltip>
  )
}
